/**
 * THE REMOVED POSTS PAGE'S WORDS AND ARITHMETIC, with no runtime imports
 * beyond the plain search helper, so a node test can load it.
 *
 * The page (pages/RemovedPosts.tsx) lists the hidden posts on the three
 * parent-facing boards and lets a Director put one back. The server side is
 * netlify/functions/boardModeration.mjs; this file only reads its answer and
 * words it for a phone.
 */

import { looseMatchWithBody } from './fuzzyMatch.ts';

export type RemovedPostBoard = 'parentBoard' | 'compParentsBoard' | 'compBoard';

/**
 * The boards whose Remove and Take this back go through the server
 * (2026-10-01): the two forums, and the Comp Team board's Parent tips.
 */
export type BoardRemoveBoard = 'parentBoard' | 'jrStaffBoard' | 'compTip';

/** The order the page draws the boards in, the server's own order. */
export const REMOVED_POST_BOARDS: readonly RemovedPostBoard[] = ['parentBoard', 'compParentsBoard', 'compBoard'];

export const REMOVED_POST_BOARD_LABELS: Readonly<Record<RemovedPostBoard, string>> = {
  parentBoard: 'Comp Parent Board',
  compParentsBoard: 'New Comp Parent Board',
  compBoard: 'Comp Team board',
};

export const REMOVED_POST_BOARD_HREFS: Readonly<Record<RemovedPostBoard, string>> = {
  parentBoard: '/community',
  compParentsBoard: '/comp-parents-board',
  compBoard: '/comp-board',
};

/** The page's own path, and the Removed posts link on every board header. */
export const REMOVED_POSTS_PATH = '/removed-posts';

/** Shown where a board does not record who hid a post. */
export const HIDDEN_BY_NOT_RECORDED = 'Not recorded';

/** The confirm step on Restore. */
export const RESTORE_CONFIRM_QUESTION = 'Put this back on the board?';

export interface RemovedPost {
  board: RemovedPostBoard;
  postId: string;
  title: string;
  firstWords: string;
  attachmentCount: number;
  authorName: string;
  postedAt: number | null;
  hiddenAt: number | null;
  replyCount: number;
  /** True only where the board wrote down who hid it: the New board always,
   * the Comp Parent Board from 2026-10-01 on, the Comp Team board never. */
  hiddenByRecorded: boolean;
  /** That person's name, or null when it was recorded but has no name saved. */
  hiddenBy: string | null;
  /** The recorded remover is the author: she took it back herself. */
  hiddenByAuthor: boolean;
  /** N26: taken down because its author's account is switched off. No Restore while she is off. */
  pausedAccount: boolean;
}

export interface RemovedPostsAnswer {
  boards: Record<RemovedPostBoard, { posts: RemovedPost[]; more: boolean }>;
  limit: number;
  reads: number | null;
  /** N26: accounts still waiting for their Comp Parent Board posts to be tidied, up to the server's cap. */
  pausedWaiting: number;
}

function record(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' && !Array.isArray(value) ? value as Record<string, unknown> : {};
}

function text(value: unknown): string {
  return typeof value === 'string' ? value : '';
}

function millis(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

function postFrom(board: RemovedPostBoard, value: unknown): RemovedPost | null {
  const row = record(value);
  const postId = text(row.postId);
  if (!postId) return null;
  return {
    board,
    postId,
    title: text(row.title),
    firstWords: text(row.firstWords),
    attachmentCount: typeof row.attachmentCount === 'number' ? Math.max(0, row.attachmentCount) : 0,
    authorName: text(row.authorName),
    postedAt: millis(row.postedAt),
    hiddenAt: millis(row.hiddenAt),
    replyCount: typeof row.replyCount === 'number' ? Math.max(0, row.replyCount) : 0,
    hiddenByRecorded: row.hiddenByRecorded === true,
    hiddenBy: typeof row.hiddenBy === 'string' && row.hiddenBy ? row.hiddenBy : null,
    hiddenByAuthor: row.hiddenByAuthor === true,
    pausedAccount: row.pausedAccount === true,
  };
}

/** The server's answer, read defensively. Anything malformed is dropped, never drawn. */
export function parseRemovedPostsAnswer(value: unknown): RemovedPostsAnswer {
  const body = record(value);
  const boardsIn = record(body.boards);
  const boards = {} as RemovedPostsAnswer['boards'];
  for (const board of REMOVED_POST_BOARDS) {
    const entry = record(boardsIn[board]);
    const posts = Array.isArray(entry.posts)
      ? entry.posts.map((row) => postFrom(board, row)).filter((row): row is RemovedPost => row !== null)
      : [];
    posts.sort((a, b) => (b.hiddenAt ?? 0) - (a.hiddenAt ?? 0));
    boards[board] = { posts, more: entry.more === true };
  }
  return {
    boards,
    limit: typeof body.limit === 'number' ? body.limit : 50,
    reads: typeof body.reads === 'number' ? body.reads : null,
    pausedWaiting: typeof body.pausedWaiting === 'number' && body.pausedWaiting > 0 ? Math.trunc(body.pausedWaiting) : 0,
  };
}

/** What a row is called: its title, else its first words, else what it carried. */
export function removedPostHeading(post: RemovedPost): string {
  if (post.title.trim()) return post.title.trim();
  if (post.firstWords.trim()) return post.firstWords.trim();
  return post.attachmentCount > 0 ? 'A post with no words, only a photo, video or file' : 'A post with no words';
}

/** N26: why a switched-off account's post is down, in words. */
export const PAUSED_ACCOUNT_REASON = 'Taken down because this account is switched off. It comes back when the account is switched back on.';

/** N26: the paused-account queue, in words, or '' when nobody is waiting. The server counts up to 20. */
export function pausedWaitingLine(count: number): string {
  if (count <= 0) return '';
  const many = count >= 20 ? '20 or more accounts are' : count === 1 ? '1 account is' : `${count} accounts are`;
  return `${many} still waiting to be tidied on the Comp Parent Board. The Hub has started on them; Refresh in a few minutes.`;
}

/** Who hid it, in words. */
export function removedPostHiddenBy(post: RemovedPost): string {
  if (!post.hiddenByRecorded) return HIDDEN_BY_NOT_RECORDED;
  const name = post.hiddenBy ?? 'An account with no name saved';
  return post.hiddenByAuthor ? `${name}, who wrote it` : name;
}

/** Search over what a Director would type: the words, who wrote it, who hid it, the board. */
export function removedPostMatches(post: RemovedPost, query: string): boolean {
  return looseMatchWithBody(
    [post.title, post.authorName, post.hiddenBy, REMOVED_POST_BOARD_LABELS[post.board]],
    [post.firstWords],
    query
  );
}

/**
 * A refused call, carrying the server's code beside the words, so the page can
 * tell "somebody already put it back" (drop the stale row) from a real failure.
 */
export class RemovedPostsCallError extends Error {
  readonly code: string | null;
  readonly status: number;
  constructor(status: number, code: string | null) {
    super(removedPostsErrorMessage(status, code));
    this.name = 'RemovedPostsCallError';
    this.status = status;
    this.code = code;
  }
}

/** True when the post is no longer hidden or no longer there: the row is stale, not broken. */
export function isStaleRestoreError(error: unknown): boolean {
  return error instanceof RemovedPostsCallError && (error.code === 'not_hidden' || error.code === 'not_found');
}

/** A failed call, in words a Director can act on. */
export function removedPostsErrorMessage(status: number, code: string | null): string {
  if (code === 'director_only') return 'Removed posts are for Directors.';
  if (code === 'sign_in_required') return 'Sign in again, then try once more.';
  if (code === 'too_many_requests') return 'That was a lot of taps in a few minutes. Wait a moment, then try again.';
  if (code === 'not_hidden') return 'That post is already back on the board.';
  if (code === 'not_found') return 'That post is not there any more, so there is nothing to put back.';
  if (code === 'author_paused') return 'The person who wrote this has their account switched off, so it cannot go back on the board while it is off. It comes back when the account is switched back on.';
  if (code === 'live_taken') return 'A newer post is on the board in this one\'s place, so it cannot go back right now. Its words are still kept here.';
  if (status === 0) return 'Could not reach the Hub. Check the connection and try again.';
  return 'Something went wrong. Try again in a moment.';
}

/**
 * A refused Remove or Take this back, in words for the board it happened on.
 * Only a Director removes somebody else's words (Bobbie, 2026-10-01: "Only
 * directors can remove other people's posts"), so that is what a refusal
 * says; an older screen that still offered the button learns it here.
 */
export function boardRemoveErrorMessage(status: number, code: string | null, what: 'post' | 'reply' | 'tip'): string {
  if (code === 'not_allowed') return `Only a Director can remove someone else's ${what}.`;
  if (code === 'not_found') return `That ${what} is not there any more.`;
  if (code === 'sign_in_required') return 'Sign in again, then try once more.';
  if (code === 'too_many_requests') return 'That was a lot of taps in a few minutes. Wait a moment, then try again.';
  if (status === 0) return 'Could not reach the Hub. Check the connection and try again.';
  return 'Something went wrong. Try again in a moment.';
}
